import * as React from 'react'
import { Box, Button, TextField } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import {
  IMasterGroups,
  IProductData,
  useGroupedMasters,
} from '../utility/TreeHelpers'
import { CurriculaButton } from './CurriculaButton'

const CurriculaGroup = React.memo(function CurriculaGroup({
  title,
  products,
  expanded,
  onToggle,
  onSelect,
  activatePopUp,
  popUpText,
  selectedTreeIds,
}: {
  title: string
  products: IProductData[]
  expanded: boolean
  onToggle: (title: string) => void
  onSelect: (selection: {
    treeId: string
    productId: number
    treeVersion: string
    masterTitle: string
  }) => void
  activatePopUp?: boolean
  popUpText?: string
  selectedTreeIds?: Set<string>
}) {
  return (
    <Box key={title} padding={'5px'}>
      <Button
        onClick={() => onToggle(title)}
        sx={{
          textTransform: 'none',
          fontWeight: 'bold',
          color: 'text.primary',
        }}
        startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      >
        {`${title} (${products.length})`}
      </Button>
      {expanded && (
        <Box
          display={'flex'}
          flexDirection={'column'}
          padding={'3px'}
          style={{ overflowY: 'auto', maxHeight: '300px' }}
        >
          {products.map((product) => {
            return (
              <CurriculaButton
                key={product.productID}
                onSelect={onSelect}
                treeId={product.treeID}
                productId={product.productID}
                activatePopUp={activatePopUp || false}
                popUpText={popUpText}
                isSelected={selectedTreeIds?.has(product.treeID)}
              />
            )
          })}
        </Box>
      )}
    </Box>
  )
})

export function CurriculaSelection({
  onSelect,
  excluded,
  activatePopUp,
  popUpText,
  selectedTreeIds,
}: {
  onSelect: (selection: {
    treeId: string
    productId: number
    treeVersion: string
    masterTitle: string
  }) => void
  excluded: string[]
  activatePopUp?: boolean
  popUpText?: string
  selectedTreeIds?: Set<string>
}) {
  const getGroupedMasters = useGroupedMasters()
  const [availableGroups, setAvailableGroups] = React.useState<IMasterGroups>(
    {},
  )
  const [other, setOther] = React.useState<IProductData[]>([])
  const [searchText, setSearchText] = React.useState('')
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(
    new Set(),
  )

  React.useEffect(() => {
    const { grouped, notGrouped } = getGroupedMasters(excluded)
    setAvailableGroups(grouped)
    setOther(notGrouped)
  }, [excluded, getGroupedMasters])

  const toggleGroup = React.useCallback((title: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(title)) {
        next.delete(title)
      } else {
        next.add(title)
      }
      return next
    })
  }, [])

  const filterProducts = React.useCallback(
    (products: IProductData[]) => {
      if (!searchText.trim()) return products
      const lower = searchText.toLowerCase()
      return products.filter((p) => p.title.toLowerCase().includes(lower))
    },
    [searchText],
  )

  // Auto-expand groups that have matching results when searching
  React.useEffect(() => {
    if (!searchText.trim()) return
    const lower = searchText.toLowerCase()
    const matching = new Set<string>()
    for (const [title, products] of Object.entries(availableGroups)) {
      if (products.some((p) => p.title.toLowerCase().includes(lower))) {
        matching.add(title)
      }
    }
    if (other.some((p) => p.title.toLowerCase().includes(lower))) {
      matching.add('Not Grouped')
    }
    setExpandedGroups(matching)
  }, [searchText, availableGroups, other])

  return (
    <Box display={'flex'} flexDirection={'column'}>
      <Box padding={'5px'}>
        <TextField
          size="small"
          placeholder="Search curricula..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          fullWidth
          sx={{ mb: 1 }}
        />
      </Box>
      {Object.keys(availableGroups).map((title) => {
        const filtered = filterProducts(availableGroups[title])
        if (filtered.length === 0) return null
        return (
          <CurriculaGroup
            key={title}
            title={title}
            products={filtered}
            expanded={expandedGroups.has(title)}
            onToggle={toggleGroup}
            onSelect={onSelect}
            activatePopUp={activatePopUp}
            popUpText={popUpText}
            selectedTreeIds={selectedTreeIds}
          />
        )
      })}
      {filterProducts(other).length > 0 && (
        <CurriculaGroup
          title={'Not Grouped'}
          products={filterProducts(other)}
          expanded={expandedGroups.has('Not Grouped')}
          onToggle={toggleGroup}
          onSelect={onSelect}
          activatePopUp={activatePopUp}
          popUpText={popUpText}
          selectedTreeIds={selectedTreeIds}
        />
      )}
    </Box>
  )
}
